'use client'

import { useState, useEffect } from 'react'
import { Bell, Mail, MessageSquare, Home, FileText, CreditCard, Shield, CheckCircle2, Loader2, Calendar, AlertCircle } from 'lucide-react'
import { getApiUrl } from '@/lib/apiUrl'

interface NotificationPreferencesProps {
  userId: string
  onUpdate?: () => void
}

interface NotificationPreferences {
  emailNotifications: boolean
  smsNotifications: boolean
  notificationPreferences: {
    welcome: boolean
    newPropertyListed: boolean
    applicationReceived: boolean
    applicationStatusChange: boolean
    newMessage: boolean
    viewingAppointment: boolean
    maintenanceRequest: boolean
    propertyVerified: boolean
    kycStatus: boolean
    paymentSuccess: boolean
    paymentFailed: boolean
    escrowReleased: boolean
  }
}

const notificationLabels: Record<keyof NotificationPreferences['notificationPreferences'], { label: string; description: string; icon: any }> = {
  welcome: {
    label: 'Welcome Emails',
    description: 'Receive welcome emails when you join the platform',
    icon: Mail
  },
  newPropertyListed: {
    label: 'New Property Listings',
    description: 'Get notified when new properties matching your interests are listed',
    icon: Home
  },
  applicationReceived: {
    label: 'Application Received',
    description: 'Notifications when someone applies for your property',
    icon: FileText
  },
  applicationStatusChange: {
    label: 'Application Updates',
    description: 'Updates on your property applications (approved, rejected, etc.)',
    icon: FileText
  },
  newMessage: {
    label: 'New Messages',
    description: 'Email notifications when you receive new messages',
    icon: MessageSquare
  },
  viewingAppointment: {
    label: 'Viewing Appointments',
    description: 'Notifications about property viewing appointments',
    icon: Calendar
  },
  maintenanceRequest: {
    label: 'Maintenance Requests',
    description: 'Updates on maintenance requests and their status',
    icon: Home
  },
  propertyVerified: {
    label: 'Property Verification',
    description: 'Notifications when your properties are verified or need attention',
    icon: Shield
  },
  kycStatus: {
    label: 'KYC Status Updates',
    description: 'Updates on your KYC verification status',
    icon: Shield
  },
  paymentSuccess: {
    label: 'Payment Success',
    description: 'Confirmations when payments are successfully processed',
    icon: CreditCard
  },
  paymentFailed: {
    label: 'Payment Failed',
    description: 'Alerts when payments fail or need attention',
    icon: CreditCard
  },
  escrowReleased: {
    label: 'Escrow Released',
    description: 'Notifications when escrow payments are released',
    icon: CreditCard
  }
}

export default function NotificationPreferences({ userId, onUpdate }: NotificationPreferencesProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    emailNotifications: true,
    smsNotifications: false,
    notificationPreferences: {
      welcome: true,
      newPropertyListed: true,
      applicationReceived: true,
      applicationStatusChange: true,
      newMessage: true,
      viewingAppointment: true,
      maintenanceRequest: true,
      propertyVerified: true,
      kycStatus: true,
      paymentSuccess: true,
      paymentFailed: true,
      escrowReleased: true
    }
  })

  useEffect(() => {
    loadPreferences()
  }, [userId])

  const loadPreferences = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('Please log in to view notification preferences')
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/users/notification-preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to load preferences: ${response.statusText}`)
      }

      const data = await response.json()
      if (data.preferences) {
        setPreferences(data.preferences)
      }
    } catch (error) {
      console.error('Error loading preferences:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to load notification preferences'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setMessage(null)
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('Please log in to update preferences')
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/users/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to update preferences: ${response.statusText}`)
      }

      setMessage({
        type: 'success',
        text: 'Notification preferences updated successfully!'
      })

      if (onUpdate) {
        onUpdate()
      }

      // Clear success message after 3 seconds
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Error saving preferences:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to update notification preferences'
      })
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = (key: keyof NotificationPreferences['notificationPreferences']) => {
    setPreferences(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        [key]: !prev.notificationPreferences[key]
      }
    }))
  }

  const handleGlobalToggle = (key: 'emailNotifications' | 'smsNotifications') => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display text-stone-900 flex items-center gap-2">
          <Bell className="w-6 h-6" />
          Notification Preferences
        </h2>
        <p className="text-stone-600 mt-1">
          Choose which email notifications you want to receive
        </p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Global Settings */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Global Settings</h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer group">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-stone-500" />
              <div>
                <div className="font-medium text-stone-900">Email Notifications</div>
                <div className="text-sm text-stone-600">
                  Master switch for all email notifications
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleGlobalToggle('emailNotifications')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.emailNotifications ? 'bg-emerald-600' : 'bg-stone-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer group">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-stone-500" />
              <div>
                <div className="font-medium text-stone-900">SMS Notifications</div>
                <div className="text-sm text-stone-600">
                  Receive notifications via SMS (coming soon)
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleGlobalToggle('smsNotifications')}
              disabled
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.smsNotifications ? 'bg-emerald-600' : 'bg-stone-300'
              } opacity-50 cursor-not-allowed`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.smsNotifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </div>
      </div>

      {/* Individual Notification Preferences */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Email Notification Types</h3>
        <div className="space-y-3">
          {Object.entries(notificationLabels).map(([key, { label, description, icon: Icon }]) => {
            const notificationKey = key as keyof NotificationPreferences['notificationPreferences']
            const isEnabled = preferences.notificationPreferences[notificationKey]
            const isDisabled = !preferences.emailNotifications

            return (
              <label
                key={key}
                className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                  isDisabled
                    ? 'border-stone-200 bg-stone-50 opacity-60 cursor-not-allowed'
                    : isEnabled
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-stone-200 bg-white hover:bg-stone-50'
                } cursor-pointer group`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <Icon className={`w-5 h-5 ${isEnabled ? 'text-emerald-600' : 'text-stone-400'}`} />
                  <div className="flex-1">
                    <div className={`font-medium ${isEnabled ? 'text-stone-900' : 'text-stone-600'}`}>
                      {label}
                    </div>
                    <div className="text-sm text-stone-600">{description}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !isDisabled && handleToggle(notificationKey)}
                  disabled={isDisabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isEnabled ? 'bg-emerald-600' : 'bg-stone-300'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            )
          })}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Save Preferences
            </>
          )}
        </button>
      </div>
    </div>
  )
}
